/**
 * /invite/:inviteId — 招待リンクの着地ページ（未認証可）。
 *
 * 仕様書: docs/01_spec/spec_user_management.md U-8
 *
 * 動作:
 *   1. getInvitationPublic Callable で招待情報を取得（未認証 OK）
 *   2. 招待情報（招待者・権限・期限）を表示
 *   3. 「Google でサインイン」ボタン
 *   4. サインインが完了したら、useAuth が acceptInvitation を自動実行
 *   5. 自動的に / にリダイレクト
 */
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { auth, functions } from '@/lib/firebase';

interface InvitationPublic {
  inviteId: string;
  email: string;
  fullName: string | null;
  dept: string;
  level: string;
  isOwner: boolean;
  status: string;
  expired: boolean;
  expiresAt: string | null;
  inviterName: string | null;
  inviterEmail: string | null;
  roleLabel: string;
}

interface GetInvitationPublicResponse {
  ok: boolean;
  invitation: InvitationPublic;
}

const getInvitationPublicFn = httpsCallable<
  { inviteId: string },
  GetInvitationPublicResponse
>(functions, 'getInvitationPublic');

export function InvitePage() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();
  const [invitation, setInvitation] = useState<InvitationPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  // 招待情報の取得
  useEffect(() => {
    if (!inviteId) {
      setError('招待 ID が指定されていません');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await getInvitationPublicFn({ inviteId });
        if (cancelled) return;
        setInvitation(result.data.invitation);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  // useAuth が signed-in（acceptInvitation 完了後）になったら / にリダイレクト
  useEffect(() => {
    if (authStatus === 'signed-in') {
      navigate('/', { replace: true });
    }
  }, [authStatus, navigate]);

  const handleSignIn = async () => {
    setError(null);
    setSigningIn(true);
    try {
      const provider = new GoogleAuthProvider();
      // 招待先メールでログインするようにヒントを渡す
      provider.setCustomParameters({
        prompt: 'select_account',
        login_hint: invitation?.email ?? '',
      });
      await signInWithPopup(auth, provider);
      // onAuthStateChanged で navigate 済み
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[var(--bg-root)]">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold">
            G
          </div>
          <div>
            <div className="font-bold text-[var(--text-primary)]">GoEnHub</div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              Admin Console
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-sm text-[var(--text-muted)]">
            招待情報を読み込んでいます...
          </div>
        )}

        {!loading && error && (
          <div className="space-y-3">
            <h1 className="text-lg font-bold text-[var(--accent-red)]">招待を表示できません</h1>
            <p className="text-sm text-[var(--text-secondary)]">{error}</p>
            <p className="text-xs text-[var(--text-muted)]">
              リンクが破損している、招待が取り消された、または有効期限が切れた可能性があります。owner に連絡してください。
            </p>
          </div>
        )}

        {!loading && invitation && !error && (
          <div className="space-y-5">
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)] mb-1">
                管理者として招待されています
              </h1>
              <p className="text-sm text-[var(--text-secondary)]">
                {invitation.fullName ? `${invitation.fullName} 様、` : ''}
                {invitation.inviterName ?? invitation.inviterEmail ?? 'owner'} から GoEnHub
                管理コンソールの管理者として招待が届きました。
              </p>
            </div>

            <dl className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded p-3 text-sm space-y-2">
              <div className="flex">
                <dt className="w-24 text-[var(--text-muted)]">メール</dt>
                <dd className="flex-1 text-[var(--text-primary)] break-all">{invitation.email}</dd>
              </div>
              <div className="flex">
                <dt className="w-24 text-[var(--text-muted)]">権限</dt>
                <dd className="flex-1 text-[var(--text-primary)]">{invitation.roleLabel}</dd>
              </div>
              {invitation.expiresAt && (
                <div className="flex">
                  <dt className="w-24 text-[var(--text-muted)]">有効期限</dt>
                  <dd className="flex-1 text-[var(--text-primary)]">
                    {new Date(invitation.expiresAt).toLocaleString('ja-JP')}
                  </dd>
                </div>
              )}
            </dl>

            {invitation.expired ? (
              <div className="bg-[var(--accent-red-subtle)] text-[var(--accent-red)] text-sm rounded p-3">
                この招待は有効期限が切れています、または取り消されました。owner に再送をご依頼ください。
              </div>
            ) : invitation.status === 'accepted' ? (
              <div className="bg-[var(--accent-green-subtle)] text-[var(--accent-green)] text-sm rounded p-3">
                この招待は受諾済みです。サインインしてダッシュボードへお進みください。
              </div>
            ) : (
              <p className="text-xs text-[var(--text-muted)]">
                招待を承認するには、上記メールアドレス（<strong className="text-[var(--text-secondary)]">{invitation.email}</strong>）と同じ Google アカウントで以下からサインインしてください。
              </p>
            )}

            <button
              type="button"
              onClick={handleSignIn}
              disabled={signingIn || invitation.expired}
              className="w-full inline-flex items-center justify-center gap-2 rounded bg-white text-gray-800 font-medium py-2.5 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {/* Google G ロゴ（SVG） */}
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.63-.06-1.25-.17-1.84H9v3.49h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.56 2.69-3.87 2.69-6.65z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.46-.81 5.95-2.18l-2.9-2.27c-.81.54-1.83.87-3.05.87-2.34 0-4.33-1.58-5.04-3.71H.96v2.34A9 9 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.96 10.71c-.18-.54-.28-1.12-.28-1.71s.1-1.17.28-1.71V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3-2.34z" fill="#FBBC05"/>
                <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3 2.34C4.67 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {signingIn ? 'サインイン中...' : 'Google アカウントでサインイン'}
            </button>

            <p className="text-[11px] text-[var(--text-muted)] text-center">
              Google アカウント未作成の場合は、先に <a href="https://accounts.google.com" target="_blank" rel="noreferrer" className="underline">accounts.google.com</a> で作成してください。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
